import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Clarity from '@microsoft/clarity';
import { flushTrackingQueue, trackPageView } from '@/lib/analytics';
import { useCookieConsent } from '@/context/CookieConsentContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

const clarityProjectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID || 'x5fag9pu6b';
const CLARITY_VISITOR_ID_KEY = 'tq_clarity_visitor_id';
const CLARITY_SESSION_ID_KEY = 'tq_clarity_session_id';

const createClarityId = (prefix) => {
  const randomValue =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `${prefix}-${randomValue}`;
};

const getStoredBrowserId = (storage, key, prefix) => {
  if (!storage) return '';

  try {
    const existingId = storage.getItem(key);
    if (existingId) return existingId;

    const nextId = createClarityId(prefix);
    storage.setItem(key, nextId);
    return nextId;
  } catch {
    return '';
  }
};

const getCustomerFriendlyName = (user, profile) => {
  const profileName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  return profileName || user?.displayName || (user?.uid ? 'Signed-in customer' : 'Guest customer');
};

export default function PixelTracker() {
  const router = useRouter();
  const { hasTrackingConsent, isReady } = useCookieConsent();
  const { user, profile } = useCustomerAuth() || {};
  const isAdminRoute = router.route === '/admin';

  useEffect(() => {
    const projectId = clarityProjectId;

    if (!isReady || isAdminRoute || !projectId) return;

    Clarity.init(projectId);
    Clarity.consentV2({
      ad_Storage: hasTrackingConsent ? 'granted' : 'denied',
      analytics_Storage: hasTrackingConsent ? 'granted' : 'denied',
    });

    if (hasTrackingConsent && typeof window !== 'undefined') {
      const customId = user?.uid
        ? `customer-${user.uid}`
        : getStoredBrowserId(window.localStorage, CLARITY_VISITOR_ID_KEY, 'visitor');
      const customSessionId = getStoredBrowserId(window.sessionStorage, CLARITY_SESSION_ID_KEY, 'session');
      const customPageId = `${window.location.pathname}${window.location.search}`;
      const friendlyName = getCustomerFriendlyName(user, profile);

      if (customId) {
        Clarity.identify(customId, customSessionId || undefined, customPageId, friendlyName);
      }

      Clarity.setTag('customer_type', user?.uid ? 'signed_in' : 'guest');
      Clarity.setTag('page_path', customPageId || '/');
      Clarity.setTag('page_route', router.route || 'unknown');
      Clarity.setTag('customer_segments', [
        user?.uid ? 'signed_in' : 'guest',
        profile ? 'profile_loaded' : 'profile_missing',
      ]);
    }
  }, [
    hasTrackingConsent,
    isAdminRoute,
    isReady,
    profile,
    router.asPath,
    router.route,
    user,
  ]);

  useEffect(() => {
    if (!isReady || isAdminRoute) return undefined;

    trackPageView();
    const flushTimers = [0, 500, 1500].map((delay) =>
      globalThis.setTimeout(() => {
        flushTrackingQueue();
      }, delay)
    );
    let routeChangeTimer = null;

    const handleRouteChange = () => {
      routeChangeTimer = globalThis.setTimeout(() => {
        trackPageView();
        flushTrackingQueue();
      }, 0);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      flushTimers.forEach((timer) => globalThis.clearTimeout(timer));
      if (routeChangeTimer !== null) {
        globalThis.clearTimeout(routeChangeTimer);
      }
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [isAdminRoute, isReady, router.events]);

  if (!isReady || isAdminRoute) {
    return null;
  }

  return <></>;
}
